import React from "react";
import AnimalSymbol from "./AnimalSymbol";
import { emptyAncestor } from "@/lib/quality";
import {
  CONDITION_FIELDS,
  ancestorHasField,
  repeatedChipsForAncestor,
} from "@/lib/conditions";

/** Tiny coloured dots in the corner — one per condition field that has data
 *  for this ancestor, regardless of whether it's repeated elsewhere. */
const ConditionDots = ({ ancestor, compact }) => {
  const active = CONDITION_FIELDS.filter((f) => ancestorHasField(ancestor, f.key));
  if (active.length === 0) return null;
  const size = compact ? 5 : 7;
  return (
    <div
      className="absolute top-1 right-1 flex flex-col gap-[2px]"
      data-testid="condition-dots"
    >
      {active.map((f) => (
        <span
          key={f.key}
          className="rounded-full border border-black/15"
          style={{ width: size, height: size, backgroundColor: f.color }}
          title={`${f.label}: ${(ancestor[f.key] || "").trim()}`}
          data-testid={`dot-${f.key}`}
        />
      ))}
    </div>
  );
};

/** Coloured chips for conditions that repeat across the whole pedigree —
 *  the actionable "trend at a glance" view. */
const RepeatedChips = ({ chips, compact }) => {
  if (!chips || chips.length === 0) return null;
  const size = compact ? "text-[7px] px-[3px] py-0" : "text-[8px] px-1 py-[1px]";
  return (
    <div
      className="flex flex-wrap gap-[2px] justify-center mt-0.5 leading-tight"
      data-testid="repeated-chips"
    >
      {chips.slice(0, compact ? 2 : 4).map((c, i) => (
        <span
          key={i}
          className={`rounded-sm font-display font-semibold text-white whitespace-nowrap ${size}`}
          style={{ backgroundColor: c.color, maxWidth: "100%" }}
          title={`${c.originalText} · seen in ${c.count} ancestors`}
          data-testid={`chip-${c.fieldKey}`}
        >
          {c.label}
          {c.count > 2 && <span className="opacity-80 ml-0.5">×{c.count}</span>}
        </span>
      ))}
      {chips.length > (compact ? 2 : 4) && (
        <span className={`text-[var(--app-text-soft)] ${size}`}>+{chips.length - (compact ? 2 : 4)}</span>
      )}
    </div>
  );
};

export const PedigreeNode = ({
  index, ancestor, onClick, selected, width, height, symbolSize,
  showText = true, label, generation = 0, repeatedMap,
}) => {
  const a = ancestor || emptyAncestor();
  const empty = !a.name;
  const isSubject = index === 0;
  const compact = generation >= 5;
  const chips = repeatedChipsForAncestor(a, repeatedMap);

  // Progressive font sizing — each generation steps down ~1px so the
  // smaller boxes in later generations don't clip the text.
  const NAME_SIZES = ["13px", "11px", "10px", "9px", "8px", "8px"];
  const REG_SIZES  = ["9px",  "8px",  "8px",  "7px", "7px", "7px"];
  const LINE_HEIGHTS = ["14px", "12px", "11px", "10px", "9px", "9px"];
  const nameFs = isSubject ? "13px" : NAME_SIZES[generation] || "8px";
  const nameLh = isSubject ? "15px" : LINE_HEIGHTS[generation] || "9px";
  const regFs = REG_SIZES[generation] || "7px";

  return (
    <button
      type="button"
      data-testid={`pedigree-node-${index}`}
      data-empty={empty}
      onClick={() => onClick(index)}
      className={`node-card group bg-white border ${
        selected ? "selected" : "border-[var(--app-border)]"
      } rounded-md shadow-sm flex flex-col items-center justify-center p-1 outline-none focus:ring-2 focus:ring-blue-500/40 relative`}
      style={{ width, height }}
    >
      <ConditionDots ancestor={a} compact={compact} />
      <AnimalSymbol parts={a.parts} size={symbolSize} />
      {showText && (
        <div className="w-full text-center mt-0.5 leading-tight px-1">
          <div
            className="truncate font-display font-semibold text-[var(--app-text)]"
            style={{ fontSize: nameFs, lineHeight: nameLh }}
            title={a.name || label}
          >
            {a.name || label}
          </div>
          {a.registration && (
            <div
              className="truncate font-mono-tech text-[var(--app-text-soft)]"
              style={{ fontSize: regFs, lineHeight: regFs }}
            >
              {a.registration}
            </div>
          )}
        </div>
      )}
      <RepeatedChips chips={chips} compact={compact} />
    </button>
  );
};

export default PedigreeNode;
